import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../ChildrenContext.js';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DataComponentBodyFinanceBlock from './DataComponent__BodyFinanceBlock';
import DataComponentFilterFinanceBlock from './DataComponent__FilterFinanceBlock';
import DataComponentDisplayControl from './DataComponent__DisplayControl';
import DateComponentSettings from './DateComponent_Settings';
import DateComponentCurrencySettings from './DateComponent_CurrencySettings';
import Pagination from '../Pagination.js';
import query from '../../helpers/query';

const DataComponentFinanceBlock = ({
    isQuery = false,
    loading = false,
    page: pageProp,
    alignPagination = false,
    settings = false,
    fields = [],
    className = '',
    settingKey = '',
    columnData = { header: {}, body: {} },
    onAction: onActionProp,
    hideResetButton,
    headerNames,
    tableBodyTitle,
    data,
    currencySettings,
    currencySettingKey,
    currencySettingData,
    displayControl,
    multiLevelHeaderNames,
    totalPages,
    t,
    ...props
}) => {
    const [filters, setFilters] = useState({});
    const [typeDisplayControl, setTypeDisplayControl] = useState('table');
    const [page, setPage] = useState(pageProp);

    useEffect(() => {
        if (pageProp !== page) {
            setPage(pageProp || page);
        }
    }, [pageProp]);

    const onAction = useCallback((stateData) => {
        onActionProp && onActionProp(stateData);

        if (isQuery) {
            query.replace(stateData);
        }
    }, [onActionProp, isQuery]);

    const handleFirstRender = useCallback(() => {
        if (!fields.length) {
            onAction({});
        }
    }, [fields.length, onAction]);

    useEffect(() => {
        handleFirstRender();
    }, [handleFirstRender]);

    const onPageChange = useCallback((nextPage) => {
        const nextFilters = {
            ...filters,
            page: nextPage
        };

        setFilters(nextFilters);
        onAction(nextFilters);
    }, [filters, onAction]);

    const onFiltersApply = useCallback((state, type = 'filtersChange') => {
        setFilters({ ...state });
        onAction(state);
    }, [onAction]);

    const onDisplayControlChange = useCallback((type) => {
        setTypeDisplayControl(type);
    }, []);

    const getClassFooter = useCallback(() => {
        return classNames({
            'ibox-footer': true,
            'text-right': alignPagination
        });
    }, [alignPagination]);

    return (
        <div className="ibox">
            {fields.length
                ? (
                    <DataComponentFilterFinanceBlock
                        fields={fields}
                        loading={loading}
                        onAction={onFiltersApply}
                        hideResetButton={hideResetButton}
                    />
                )
                : null
            }
            <DateComponentSettings 
                settings={settings}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={settingKey}
                loading={loading}
                data={data}
                filters={filters}
            />
            <DateComponentCurrencySettings
                settings={currencySettings}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={currencySettingKey}
                loading={loading}
                data={currencySettingData}
                filters={filters}
            />
            <div className="ibox-content">
                {displayControl
                    ? <DataComponentDisplayControl
                        data={displayControl.data}
                        type={typeDisplayControl}
                        name={displayControl.name}
                        onChange={onDisplayControlChange}
                      />
                    : null
                }
                <DataComponentBodyFinanceBlock
                    data={data}
                    headerNames={headerNames}
                    multiLevelHeaderNames={multiLevelHeaderNames}
                    columnData={columnData}
                    loading={loading}
                    settings={settings}
                    settingKey={settingKey}
                    displayControl={{
                        ...displayControl,
                        type: typeDisplayControl
                    }}
                    filters={filters}
                    className={className}
                />
            </div>
            {totalPages > 1
                ? <div className={getClassFooter()}>
                    <Pagination
                        onPageChange={onPageChange}
                        initialPage={filters.page}
                        totalPages={totalPages}
                        loading={loading}
                        align={alignPagination ? 'right' : 'left'}
                    />
                  </div>
                : null
            }
        </div>
    );
};

DataComponentFinanceBlock.propTypes = {
    isQuery: PropTypes.bool,
    loading: PropTypes.bool,
    page: PropTypes.number,
    alignPagination: PropTypes.bool,
    settings: PropTypes.bool,
    fields: PropTypes.array,
    className: PropTypes.string,
    settingKey: PropTypes.string,
    columnData: PropTypes.shape({
        header: PropTypes.object,
        body: PropTypes.object
    }),
    onAction: PropTypes.func,
    hideResetButton: PropTypes.bool,
    headerNames: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    tableBodyTitle: PropTypes.string,
    data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    currencySettings: PropTypes.bool,
    currencySettingKey: PropTypes.string,
    currencySettingData: PropTypes.object,
    displayControl: PropTypes.shape({
        type: PropTypes.string,
        name: PropTypes.string,
        data: PropTypes.array
    }),
    multiLevelHeaderNames: PropTypes.array,
    totalPages: PropTypes.number,
    t: PropTypes.object
};

export default withTranslation(DataComponentFinanceBlock);
